import type { WorkspaceTaskRow } from '@/features/tasks';

import { hasState, needsPerson } from './listView';
import { leadMs } from './taskSearch';

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;
/** How far back a workflow's usual lead time looks (spec 113 FR-007). */
const USUAL_WINDOW_MS = 30 * DAY_MS;
/** Fewer finished tasks than this and a workflow has no usual time. */
const USUAL_MIN_FINISHED = 3;

/** The five figures above the Tasks page (spec 110 FR-001). */
export interface TaskTiles {
  running: number;
  /** Waiting at a gate or branch, or flagged for review or attention. */
  needsPerson: number;
  failed: number;
  /** Refused by a workspace or workflow spend limit. */
  blocked: number;
  /** Reached the done column within the last seven days. */
  completedLast7d: number;
  /** Median creation-to-done time of those; `null` when there are none. */
  medianLeadMs: number | null;
  /**
   * Waiting for a slot (spec 116 FR-002): said beneath In progress, never in it —
   * nothing is running yet, and the tile's press filters to running only.
   */
  queued: number;
  /**
   * Running and waiting together (spec 118 FR-010, superseding 116 FR-002): what the
   * Runs at once tile counts and, pressed, lists.
   */
  runsAtOnce: number;
}

/**
 * How the workspace is doing, before anyone reads a row.
 *
 * Counted over **every** row, never the narrowed list (FR-002): the tiles
 * answer "is anything wrong in this workspace", and a search for one task must
 * not make the rest of the workspace look quiet.
 *
 * The tiles are **independent questions, not a partition**. A running task
 * flagged for review is running *and* needs a person, and counting it in only
 * one would hide the other fact. Within a tile, a row counts once.
 *
 * `running` and `failed` count exactly the rows the existing state filter
 * matches for those values, which is what lets those two tiles be pressed: the
 * list a press produces has the tile's number of rows in it (FR-007).
 */
export function summarizeTaskTiles(
  rows: readonly WorkspaceTaskRow[],
  now: number,
): TaskTiles {
  let running = 0;
  let needsPersonCount = 0;
  let failed = 0;
  let blocked = 0;
  let queued = 0;
  const leads: number[] = [];

  for (const row of rows) {
    if (hasState(row, 'running')) running += 1;
    if (row.state === 'failed') failed += 1;
    if (needsPerson(row)) needsPersonCount += 1;
    if (row.blocked !== null) blocked += 1;
    if (row.queued !== null) queued += 1;

    if (row.finished && row.finishedAt !== null) {
      const finishedAt = Date.parse(row.finishedAt);
      if (finishedAt >= now - WEEK_MS) {
        leads.push(finishedAt - Date.parse(row.createdAt));
      }
    }
  }

  return {
    running,
    needsPerson: needsPersonCount,
    failed,
    blocked,
    completedLast7d: leads.length,
    medianLeadMs: median(leads),
    queued,
    runsAtOnce: running + queued,
  };
}

/**
 * Each workflow's usual lead time: the median creation-to-finish time of its
 * tasks finished in the last 30 days (spec 113 FR-007).
 *
 * Per workflow, not per workspace, because workflows differ by design — a
 * specification takes days where a bug fix takes hours, and one workspace
 * figure would mark every long workflow's tasks as late. A workflow with fewer
 * than three finished tasks has no usual time at all: two data points are an
 * anecdote, and a mark drawn from them would be noise. Hidden tasks never
 * count, as they never count in the tiles (spec 112 FR-019).
 */
export function workflowLeadMedians(
  rows: readonly WorkspaceTaskRow[],
  now: number,
): Map<string, number> {
  const byWorkflow = new Map<string, number[]>();
  for (const row of rows) {
    if (row.hidden || !row.finished || row.finishedAt === null) continue;
    const finishedAt = Date.parse(row.finishedAt);
    if (finishedAt < now - USUAL_WINDOW_MS) continue;
    const leads = byWorkflow.get(row.workflowKey) ?? [];
    leads.push(finishedAt - Date.parse(row.createdAt));
    byWorkflow.set(row.workflowKey, leads);
  }

  const medians = new Map<string, number>();
  for (const [key, leads] of byWorkflow) {
    const value = leads.length >= USUAL_MIN_FINISHED ? median(leads) : null;
    if (value !== null) medians.set(key, value);
  }
  return medians;
}

/**
 * Whether an open task has taken more than twice its workflow's usual time
 * (spec 113 FR-008). A mark for the reader only: it changes no state, filter,
 * order or tile. Strictly greater, so a task at exactly twice is not marked.
 */
export function isStale(
  row: WorkspaceTaskRow,
  medians: ReadonlyMap<string, number>,
  now: number,
): boolean {
  if (row.finishedAt !== null) return false;
  const usual = medians.get(row.workflowKey);
  return usual !== undefined && leadMs(row, now) > 2 * usual;
}

/** The middle value; the mean of the two middle values for an even count. */
function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}
